/**
 * Code & Syntax Module
 * Handles Prism syntax highlighting, line numbers, filename headers,
 * diff highlighting, collapsible blocks, Mermaid diagrams, and KaTeX math
 */

// Line count threshold for collapsible code blocks
var CODE_COLLAPSE_THRESHOLD = 25;
var CODE_COLLAPSED_HEIGHT = '400px';

function initSyntaxHighlighting() {
    'use strict';

    if (document.body.dataset.syntaxHighlighting === 'false' || typeof Prism === 'undefined') {
        return;
    }

    if (Prism.manual !== undefined) {
        Prism.manual = true;
    }

    var codeBlocks = document.querySelectorAll('.post-content pre code');
    if (codeBlocks.length === 0) {
        // Still check for mermaid/katex even without standard code blocks
        initMermaid();
        initKaTeX();
        return;
    }

    codeBlocks.forEach(function(code) {
        var pre = code.parentElement;
        if (!pre) return;

        // Parse language from class
        var language = 'plaintext';
        var codeClasses = code.className.match(/language-(\S+)/);
        if (codeClasses) {
            language = codeClasses[1];
        }

        // Skip mermaid and math blocks — they get special treatment
        if (language === 'mermaid' || language === 'math' || language === 'latex' || language === 'katex') {
            return;
        }

        // Ensure language class on pre
        if (!pre.className.includes('language-')) {
            pre.className = 'language-' + language;
            code.className = 'language-' + language;
        }

        // Enable line numbers on all code blocks
        pre.classList.add('line-numbers');

        try {
            Prism.highlightElement(code);
        } catch (e) {
            // Silently fail — non-critical feature
        }
    });

    addCodeBlockFeatures();
    initDiffHighlighting();
    initCollapsibleBlocks();
    initMermaid();
    initKaTeX();
}

/**
 * Add language labels, filename headers, and copy buttons to code blocks
 */
function addCodeBlockFeatures() {
    'use strict';

    var codeBlocks = document.querySelectorAll('.post-content pre[class*="language-"]');

    codeBlocks.forEach(function(pre) {
        // Skip if already processed
        if (pre.dataset.codeProcessed) return;
        pre.dataset.codeProcessed = 'true';

        var language = pre.className.match(/language-(\w+)/);
        var langName = language && language[1] ? language[1] : '';

        // Detect filename from Ghost code card caption
        var filename = detectFilename(pre);

        if (filename) {
            // Render filename header bar instead of language label
            var fileBar = document.createElement('div');
            fileBar.className = 'code-filename-bar';
            fileBar.innerHTML = '<span class="code-filename-icon">' + getFileIcon(filename) + '</span>' +
                '<span class="code-filename-text">' + filename + '</span>';
            pre.appendChild(fileBar);
        } else if (langName) {
            // Language label
            var label = document.createElement('div');
            label.className = 'code-language-label';
            label.textContent = langName.toUpperCase();
            pre.appendChild(label);
        }

        // Copy button
        var button = document.createElement('button');
        button.className = 'code-copy-button';
        button.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>';
        button.setAttribute('aria-label', 'Copy code');

        button.addEventListener('click', function() {
            var code = pre.querySelector('code');
            if (code) {
                navigator.clipboard.writeText(code.textContent).then(function() {
                    var checkIcon = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>';
                    var copyIcon = button.innerHTML;
                    button.innerHTML = checkIcon;
                    setTimeout(function() { button.innerHTML = copyIcon; }, 2000);
                });
            }
        });

        pre.style.position = 'relative';
        pre.appendChild(button);
    });
}

/**
 * Detect filename from Ghost code card figure/figcaption
 */
function detectFilename(pre) {
    'use strict';

    // Ghost wraps code cards in <figure> with optional <figcaption>
    var figure = pre.closest('figure');
    if (figure) {
        var caption = figure.querySelector('figcaption');
        if (caption && caption.textContent.trim()) {
            var text = caption.textContent.trim();
            // If caption looks like a filename (has extension or path separator)
            if (/\.\w+$/.test(text) || text.includes('/')) {
                caption.style.display = 'none';
                return text;
            }
        }
    }
    return null;
}

/**
 * Get file type icon SVG based on extension
 */
function getFileIcon(filename) {
    'use strict';

    var ext = filename.split('.').pop().toLowerCase();
    var iconColors = {
        'js': '#f7df1e', 'jsx': '#61dafb', 'ts': '#3178c6', 'tsx': '#3178c6',
        'py': '#3776ab', 'rb': '#cc342d', 'go': '#00add8', 'rs': '#dea584',
        'css': '#1572b6', 'html': '#e34f26', 'json': '#6b7280', 'yaml': '#cb171e',
        'yml': '#cb171e', 'md': '#083fa1', 'sh': '#4eaa25', 'bash': '#4eaa25',
        'sql': '#e38c00', 'php': '#777bb4', 'java': '#ed8b00', 'c': '#a8b9cc',
        'cpp': '#00599c', 'cs': '#239120', 'hbs': '#f0772b', 'vue': '#42b883',
        'svelte': '#ff3e00', 'dockerfile': '#2496ed', 'toml': '#9c4121'
    };
    var color = iconColors[ext] || '#9ca3af';
    return '<svg class="w-3.5 h-3.5" viewBox="0 0 16 16" fill="' + color + '"><path d="M2 1.75C2 .784 2.784 0 3.75 0h5.586c.464 0 .909.184 1.237.513l2.914 2.914c.329.328.513.773.513 1.237v9.586A1.75 1.75 0 0112.25 16h-8.5A1.75 1.75 0 012 14.25V1.75z"/></svg>';
}

/**
 * Diff highlighting — mark +/- lines with colors
 */
function initDiffHighlighting() {
    'use strict';

    var diffBlocks = document.querySelectorAll('.post-content pre.language-diff code');

    diffBlocks.forEach(function(code) {
        var lines = code.innerHTML.split('\n');
        var processed = lines.map(function(line) {
            var stripped = line.replace(/<[^>]*>/g, '');
            if (/^\+[^+]/.test(stripped) || /^\+$/.test(stripped)) {
                return '<span class="code-line-added">' + line + '</span>';
            } else if (/^-[^-]/.test(stripped) || /^-$/.test(stripped)) {
                return '<span class="code-line-removed">' + line + '</span>';
            }
            return line;
        });
        code.innerHTML = processed.join('\n');
    });
}

/**
 * Collapsible code blocks for blocks exceeding threshold
 */
function initCollapsibleBlocks() {
    'use strict';

    var codeBlocks = document.querySelectorAll('.post-content pre[class*="language-"]');
    var isEnglish = document.documentElement.lang === 'en';

    codeBlocks.forEach(function(pre) {
        var code = pre.querySelector('code');
        if (!code) return;

        var lineCount = code.textContent.split('\n').length;
        if (lineCount <= CODE_COLLAPSE_THRESHOLD) return;

        // Mark as collapsible
        pre.classList.add('code-collapsible', 'code-collapsed');
        pre.style.maxHeight = CODE_COLLAPSED_HEIGHT;
        pre.style.overflow = 'hidden';

        // Create expand/collapse overlay
        var overlay = document.createElement('div');
        overlay.className = 'code-collapse-overlay';

        var expandText = isEnglish ? 'Show all ' + lineCount + ' lines' : 'Alle ' + lineCount + ' Zeilen anzeigen';
        var collapseText = isEnglish ? 'Show less' : 'Weniger anzeigen';

        var btn = document.createElement('button');
        btn.className = 'code-collapse-btn';
        btn.textContent = expandText;
        btn.setAttribute('aria-expanded', 'false');

        btn.addEventListener('click', function() {
            var isCollapsed = pre.classList.contains('code-collapsed');
            if (isCollapsed) {
                pre.classList.remove('code-collapsed');
                pre.style.maxHeight = 'none';
                pre.style.overflow = 'auto';
                btn.textContent = collapseText;
                btn.setAttribute('aria-expanded', 'true');
                overlay.classList.add('code-collapse-overlay--expanded');
            } else {
                pre.classList.add('code-collapsed');
                pre.style.maxHeight = CODE_COLLAPSED_HEIGHT;
                pre.style.overflow = 'hidden';
                btn.textContent = expandText;
                btn.setAttribute('aria-expanded', 'false');
                overlay.classList.remove('code-collapse-overlay--expanded');
                pre.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });

        overlay.appendChild(btn);
        // Insert overlay after the pre element
        pre.insertAdjacentElement('afterend', overlay);
    });
}

/**
 * Mermaid diagram support — lazy load from CDN
 */
function initMermaid() {
    'use strict';

    var mermaidBlocks = document.querySelectorAll('.post-content pre code.language-mermaid');
    if (mermaidBlocks.length === 0) return;

    var script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js';
    script.onload = function() {
        var isDark = document.documentElement.classList.contains('dark');
        window.mermaid.initialize({
            startOnLoad: false,
            theme: isDark ? 'dark' : 'default',
            securityLevel: 'loose',
            fontFamily: 'inherit'
        });

        var isEnglish = document.documentElement.lang === 'en';

        mermaidBlocks.forEach(function(code, index) {
            var pre = code.parentElement;
            var figure = pre.closest('figure') || pre.parentElement;
            var source = code.textContent;

            // Create container
            var container = document.createElement('div');
            container.className = 'mermaid-container';

            var diagramDiv = document.createElement('div');
            diagramDiv.className = 'mermaid-diagram';

            // Source toggle button
            var toggleBtn = document.createElement('button');
            toggleBtn.className = 'mermaid-source-toggle';
            toggleBtn.textContent = isEnglish ? 'View source' : 'Quelltext anzeigen';

            var sourceDiv = document.createElement('pre');
            sourceDiv.className = 'mermaid-source hidden';
            var sourceCode = document.createElement('code');
            sourceCode.textContent = source;
            sourceDiv.appendChild(sourceCode);

            toggleBtn.addEventListener('click', function() {
                var isHidden = sourceDiv.classList.contains('hidden');
                sourceDiv.classList.toggle('hidden');
                toggleBtn.textContent = isHidden
                    ? (isEnglish ? 'Hide source' : 'Quelltext ausblenden')
                    : (isEnglish ? 'View source' : 'Quelltext anzeigen');
            });

            container.appendChild(diagramDiv);
            container.appendChild(toggleBtn);
            container.appendChild(sourceDiv);

            // Replace code block with diagram container
            var target = figure.tagName === 'FIGURE' ? figure : pre;
            target.parentElement.replaceChild(container, target);

            // Render diagram
            try {
                window.mermaid.render('mermaid-svg-' + index, source).then(function(result) {
                    diagramDiv.innerHTML = result.svg;
                });
            } catch (e) {
                diagramDiv.innerHTML = '<p class="text-red-500 text-sm">Diagram rendering failed</p>';
            }
        });
    };
    document.head.appendChild(script);
}

/**
 * KaTeX math formula support — lazy load from CDN
 */
function initKaTeX() {
    'use strict';

    var mathBlocks = document.querySelectorAll('.post-content pre code.language-math, .post-content pre code.language-latex, .post-content pre code.language-katex');
    var postContent = document.querySelector('.post-content');

    // Check for inline math delimiters
    var hasInlineMath = postContent && /\$[^$\n]+\$/.test(postContent.innerHTML);

    if (mathBlocks.length === 0 && !hasInlineMath) return;

    // Load KaTeX CSS
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16/dist/katex.min.css';
    document.head.appendChild(link);

    // Load KaTeX JS
    var script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16/dist/katex.min.js';
    script.onload = function() {
        // Render block math
        mathBlocks.forEach(function(code) {
            var pre = code.parentElement;
            var figure = pre.closest('figure') || pre.parentElement;
            var formula = code.textContent.trim();

            var container = document.createElement('div');
            container.className = 'katex-block';

            try {
                window.katex.render(formula, container, {
                    displayMode: true,
                    throwOnError: false,
                    trust: true
                });
            } catch (e) {
                container.textContent = formula;
                container.classList.add('katex-error');
            }

            var target = figure.tagName === 'FIGURE' ? figure : pre;
            target.parentElement.replaceChild(container, target);
        });

        // Render inline math ($...$)
        if (hasInlineMath) {
            var autoRender = document.createElement('script');
            autoRender.src = 'https://cdn.jsdelivr.net/npm/katex@0.16/dist/contrib/auto-render.min.js';
            autoRender.onload = function() {
                if (postContent && window.renderMathInElement) {
                    window.renderMathInElement(postContent, {
                        delimiters: [
                            { left: '$$', right: '$$', display: true },
                            { left: '$', right: '$', display: false },
                            { left: '\\(', right: '\\)', display: false },
                            { left: '\\[', right: '\\]', display: true }
                        ],
                        throwOnError: false
                    });
                }
            };
            document.head.appendChild(autoRender);
        }
    };
    document.head.appendChild(script);
}
